<script setup>
import { ref, onMounted } from "vue";
import { TrashIcon } from "@heroicons/vue/20/solid";
import axios from 'axios';

// Refs for categories data
const categories = ref([]);
const categoryName = ref("");
const draftItems = ref({});
const expandedCategoryId = ref(null);

const ensureDraft = (categoryId) => {
  if (!draftItems.value[categoryId]) {
    draftItems.value[categoryId] = { title: '', url: '' };
  }
  return draftItems.value[categoryId];
};

// Function to fetch categories from the API
const fetchData = async () => {
  try {
    const response = await fetch("https://api.meritemaritime-fnmm.com/prix/listprixanditem");
    if (!response.ok) throw new Error("Erreur lors de la récupération des données");
    categories.value = await response.json();
    categories.value.forEach((category) => ensureDraft(category.id));
    if (!expandedCategoryId.value && categories.value.length > 0) {
      expandedCategoryId.value = categories.value[0].id;
    }
  } catch (error) {
    console.error("Erreur API:", error);
  }
};

const toggleCategory = (categoryId) => {
  expandedCategoryId.value = expandedCategoryId.value === categoryId ? null : categoryId;
};

const deleteItem = async (itemId) => {
  try {
    // Envoi de la requête DELETE avec l'ID dans le body
    const response = await axios.delete('https://api.meritemaritime-fnmm.com/prix/deleteitem', {
      data: { id: itemId }, // ID dans le corps de la requête
    });
    
    if (response.status === 200) {
      console.log('Item supprimé avec succès!');
      // Re-fetch des catégories pour mettre à jour la liste après suppression
      await fetchData();
    }
  } catch (error) {
    console.error('Erreur lors de la suppression de l\'item:', error);
  }
};

const updateCategory = async (category) => {
  try {
    // Envoie de la requête PUT pour mettre à jour l'élément
    const response = await axios.put('https://api.meritemaritime-fnmm.com/prix/updateprix', {
      id: category.id,
      name: category.name,
    });

    if (response.status === 200) {
      console.log('Item mis à jour avec succès!');
    }
  } catch (error) {
    console.error('Erreur lors de la mise à jour de l\'item:', error);
  }
};


const updateItem = async (texte) => {
  try {
    // Envoie de la requête PUT pour mettre à jour l'élément
    const response = await axios.put('https://api.meritemaritime-fnmm.com/prix/updateitem', {
      id: texte.id,
      nom: texte.nom,
      img: texte.img
    });

    if (response.status === 200) {
      console.log('Item mis à jour avec succès!');
    }
  } catch (error) {
    console.error('Erreur lors de la mise à jour de l\'item:', error);
  }
};

const deletePrix = async (itemId) => {
  try {
    // Envoi de la requête DELETE avec l'ID dans le body
    const response = await axios.delete('https://api.meritemaritime-fnmm.com/prix/deleteprix', {
      data: { id: itemId }, // ID dans le corps de la requête
    });
    
    if (response.status === 200) {
      console.log('Item supprimé avec succès!');
      // Re-fetch des catégories pour mettre à jour la liste après suppression
      await fetchData();
    }
  } catch (error) {
    console.error('Erreur lors de la suppression de l\'item:', error);
  }
};




// Function to handle the category creation
const createCategory = async () => {
  try {
    const response = await fetch("https://api.meritemaritime-fnmm.com/prix/prixcreate", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ name: categoryName.value }),
    });

    if (!response.ok) throw new Error("Erreur lors de la création de la catégorie");

    // After creating the category, fetch the updated list
    await fetchData();
    
    // Optionally, reset the category name input
    categoryName.value = "";
  } catch (error) {
    console.error("Erreur API:", error);
  }
};

const submitForm = async (categoryId) => {
  try {
    const draft = ensureDraft(categoryId);

    if (!draft.title.trim() || !draft.url.trim()) {
      alert('Renseignez le titre et l\'URL avant d\'ajouter.');
      return;
    }

    // Ajouter l'ID à formData
    const dataToSend = {
      title: draft.title,
      url: draft.url,
      categoryId: categoryId // Ou tout autre ID que tu veux ajouter
    };

    const response = await axios.post('https://api.meritemaritime-fnmm.com/prix/createitem', dataToSend);

    if (response.status === 200) {
      console.log('Item ajouté avec succès!');
      draftItems.value[categoryId] = { title: '', url: '' };
      await fetchData();
    }
  } catch (error) {
    console.error('Erreur lors de l\'ajout de l\'item:', error);
  }
};


// Call the API when the component is mounted
onMounted(fetchData);
</script>

<template>
  <div class="bg-gray-900 p-6 rounded-xl border-gray-700 border shadow mb-8">
    <h1 class="text-2xl text-gray-100 mb-2">Créer une catégorie</h1>
    <p class="text-sm text-gray-400 mb-6">Etape 1: créez une catégorie (ex: Prix littéraires 2026), puis ajoutez les visuels en dessous.</p>
    <div class="col-span-full">
      <label for="title" class="block text-sm/6 font-medium text-white">Nom de la catégorie</label>
      <div class="mt-2">
        <input
          v-model="categoryName"
          type="text"
          name="title"
          id="title"
          placeholder="-"
          class="mt-2 px-4 py-4 block w-full rounded-md border-0 bg-white/5 text-white shadow-sm ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-green-500 sm:text-sm/6"
        />
      </div>
    </div>
    <div class="mt-6 flex items-center justify-end gap-x-6">
      <button
        @click="createCategory"
        type="button"
        class="rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
      >
        Créer
      </button>
    </div>
  </div>

  <div class="w-full flex flex-col">
    <ul v-if="categories.length" role="list" class="space-y-4">
      <li v-for="category in categories" :key="category.id" class="rounded-xl border border-gray-700 bg-gray-900">
        <button
          type="button"
          @click="toggleCategory(category.id)"
          class="w-full px-4 py-4 flex items-center justify-between text-left hover:bg-white/5 rounded-xl"
        >
          <div>
            <p class="text-lg font-semibold text-white">{{ category.name }}</p>
            <p class="text-xs text-gray-400">{{ category.textes_complets.length }} élément(s)</p>
          </div>
          <span class="text-sm text-indigo-300">{{ expandedCategoryId === category.id ? 'Masquer' : 'Gérer' }}</span>
        </button>

        <div v-if="expandedCategoryId === category.id" class="border-t border-gray-700 p-4 space-y-4">
          <div class="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3 items-end">
            <div>
              <label class="text-sm text-gray-200">Nom de la catégorie</label>
              <input v-model="category.name" type="text" class="mt-2 px-4 py-3 block w-full rounded-md border-0 bg-white/5 text-white shadow-sm ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-green-500 sm:text-sm/6"/>
            </div>
            <button @click="updateCategory(category)" type="button" class="rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500">
              Enregistrer la catégorie
            </button>
          </div>

          <div class="rounded-lg border border-gray-700 bg-gray-800 p-4">
            <p class="text-xs uppercase tracking-wide text-gray-400 mb-3">Ajouter un nouvel élément</p>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <input v-model="ensureDraft(category.id).title" type="text" placeholder="Titre" class="px-4 py-3 block w-full rounded-md border-0 bg-white/5 text-white ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-green-500 sm:text-sm/6"/>
              <input v-model="ensureDraft(category.id).url" type="text" placeholder="URL de l'image" class="px-4 py-3 block w-full rounded-md border-0 bg-white/5 text-white ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-green-500 sm:text-sm/6"/>
            </div>
            <div class="mt-3 flex justify-end">
              <button @click="submitForm(category.id)" type="button" class="rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500">
                Ajouter
              </button>
            </div>
          </div>

          <div class="space-y-3">
            <div v-for="texte in category.textes_complets" :key="texte.id" class="rounded-lg border border-gray-700 bg-gray-800 p-4">
              <div class="grid grid-cols-1 lg:grid-cols-[120px_1fr] gap-3 items-start">
                <img :src="texte.img" class="rounded-lg border border-gray-700 w-[120px] h-[90px] object-cover" />
                <div class="space-y-3">
                  <input v-model="texte.nom" type="text" placeholder="Titre" class="px-4 py-3 block w-full rounded-md border-0 bg-white/5 text-white ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-green-500 sm:text-sm/6"/>
                  <input v-model="texte.img" type="text" placeholder="URL image" class="px-4 py-3 block w-full rounded-md border-0 bg-white/5 text-white ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-green-500 sm:text-sm/6"/>
                  <div class="flex justify-end gap-2">
                    <button @click="updateItem(texte)" type="button" class="rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500">
                      Enregistrer
                    </button>
                    <button @click="deleteItem(texte.id)" type="button" class="rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500">
                      <TrashIcon class="w-5 h-5 shrink-0 text-gray-200" aria-hidden="true"/>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="flex justify-end">
            <button @click="deletePrix(category.id)" type="button" class="rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500">
              Supprimer la catégorie
            </button>
          </div>
        </div>
      </li>
    </ul>

    <div v-else class="rounded-lg border border-gray-700 bg-gray-900 px-4 py-6 text-sm text-gray-400">
      Aucune catégorie pour le moment.
    </div>
  </div>
</template>
